import { useState } from 'react';

import { toast } from 'sonner';

import { Alert, AlertDescription } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';

import { AccountLockedIcon } from '@/entities/account';
import { AuthCard } from '@/shared/ui/auth/auth-card';

import { requestUnlockEmail } from '../api/request-unlock-email';
import { decodeUnlockTokenEmail } from '../lib/decode-unlock-token-email';

type Props = {
    token: string;
    onResent: (email: string | null) => void;
};

export function UnlockInvalidLink({ token, onResent }: Props) {
    const [isRequesting, setIsRequesting] = useState(false);

    const handleRequest = async () => {
        setIsRequesting(true);
        const result = await requestUnlockEmail(token);
        setIsRequesting(false);
        if (!result.ok) {
            toast.error('Something went wrong sending the email. Please try again.');
            return;
        }

        // The parent clears the expired token from the URL and shows the locked screen. A
        // composite token yields the email to interpolate; a legacy/opaque one yields null
        // and the locked screen falls back to generic copy. Either way the user moves on.
        onResent(decodeUnlockTokenEmail(token));
    };

    return (
        <div className="flex flex-1 items-start justify-center px-4">
            <AuthCard size="narrow" className="mt-28">
                <div className="flex flex-col gap-6">
                    <AccountLockedIcon />

                    <h1 className="text-3xl font-bold md:text-4xl">Your account has been locked</h1>

                    <Alert variant="destructive">
                        <AlertDescription>
                            <p>
                                <span className="font-bold">Unlock link expired.</span> Send new
                                email below
                            </p>
                        </AlertDescription>
                    </Alert>

                    <Button
                        size="lg"
                        className="w-full"
                        onClick={handleRequest}
                        disabled={isRequesting}
                    >
                        {isRequesting ? 'Sending…' : 'Send New Unlock Email'}
                    </Button>
                </div>
            </AuthCard>
        </div>
    );
}
